import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type {
  Assessment,
  AssessmentDetail,
  AssessmentTemplate,
  AssessmentListFacets,
  AssessmentHistoryEvent,
  AssessmentFilters,
  AssessmentOverview,
  IssueAssessmentDto,
  ReturnAssessmentDto,
  SectionScore,
} from '@/api/types/assessment.types';

// ── List ──────────────────────────────────────────────────────────────────────

export function useAssessments(filters?: AssessmentFilters, page = 1, perPage = 25) {
  const { tenantId } = useAuthStore();
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('per_page', String(perPage));
  if (filters?.search) params.set('search', filters.search);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.tier) params.set('tier', filters.tier);
  if (filters?.analyst_id) params.set('analyst_id', filters.analyst_id);
  // ⚠ the server reads `overdue` (not `overdue_only`) — send the key the api actually honors.
  if (filters?.overdue_only) params.set('overdue', 'true');
  if (filters?.vendor_id) params.set('vendor_id', filters.vendor_id);
  if (filters?.sort) params.set('sort', filters.sort);
  if (filters?.sort_dir) params.set('sort_dir', filters.sort_dir);
  if (filters?.outcome) params.set('outcome', filters.outcome);

  return useQuery({
    queryKey: ['assessments', tenantId, filters, page, perPage],
    queryFn: async () => {
      const { data } = await apiClient.get(`/assessments?${params.toString()}`);
      return data as { data: Assessment[]; meta: { total: number; page: number; per_page: number; total_pages: number }; facets?: AssessmentListFacets };
    },
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

// Tenant-wide operations-workspace overview (KPI row + 12-state lifecycle strip).
export function useAssessmentsOverview() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['assessments-overview', tenantId],
    queryFn: async (): Promise<AssessmentOverview> => {
      const { data } = await apiClient.get<{ data: AssessmentOverview }>(`/assessments/overview`);
      return data.data;
    },
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

// ── Single ────────────────────────────────────────────────────────────────────

export function useAssessment(id: string | undefined) {
  return useQuery({
    queryKey: ['assessment', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/assessments/${id}`);
      return data.data as AssessmentDetail;
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

// Per-section score rollup (post-scoring). The assessment payload does NOT carry
// weighted_contribution/status, so the scored lifecycle-band variant lazily fetches this
// existing endpoint (pass enabled=false until scored to avoid fetching pre-scoring).
export function useAssessmentSectionScores(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['assessment-section-scores', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/assessments/${id}/section-scores`);
      return data.data as SectionScore[];
    },
    enabled: !!id && enabled,
    staleTime: 60_000,
  });
}

// ── Templates ─────────────────────────────────────────────────────────────────

export function useAssessmentTemplates() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['assessment-templates', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/assessment-templates?is_hub_template=false');
      return data.data as AssessmentTemplate[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

// ── History ───────────────────────────────────────────────────────────────────

export function useAssessmentHistory(id: string | undefined) {
  return useQuery({
    queryKey: ['assessment-history', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/assessments/${id}/history`);
      return data.data as AssessmentHistoryEvent[];
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

// ── Create (Issue) ────────────────────────────────────────────────────────────

export function useCreateAssessment() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (dto: IssueAssessmentDto) => {
      const { data } = await apiClient.post('/assessments', dto);
      return data.data as Assessment;
    },
    onSuccess: (assessment) => {
      qc.invalidateQueries({ queryKey: ['assessments', tenantId] });
      navigate(`/assessments/${assessment.id}`);
    },
  });
}

// ── Approve ───────────────────────────────────────────────────────────────────

export function useApproveAssessment(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { notes: string }) => {
      const { data } = await apiClient.patch(`/assessments/${assessmentId}/approve`, dto);
      return data.data as Assessment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment', assessmentId] });
      qc.invalidateQueries({ queryKey: ['assessments'] });
    },
  });
}

// ── Send Back / Return (VA.2.4) ───────────────────────────────────────────────

export function useSendBackAssessment(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: ReturnAssessmentDto) => {
      const { data } = await apiClient.post(`/assessments/${assessmentId}/sendback`, dto);
      return data.data as Assessment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment', assessmentId] });
      qc.invalidateQueries({ queryKey: ['assessments'] });
    },
  });
}

// ── Cancel Assessment (RM; every active state, required reason) ───────────────

export function useCancelAssessment(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { reason: string }) => {
      const { data } = await apiClient.patch(`/assessments/${assessmentId}/cancel`, dto);
      return data.data as Assessment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment', assessmentId] });
      qc.invalidateQueries({ queryKey: ['assessment-history', assessmentId] });
      qc.invalidateQueries({ queryKey: ['assessments'] });
      qc.invalidateQueries({ queryKey: ['assessments-overview'] });
    },
  });
}

// ── Resend Assessment (VAC.2) ─────────────────────────────────────────────────

export function useResendAssessment(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { recipient_ids: string[]; message?: string }) => {
      const { data } = await apiClient.post(`/assessments/${assessmentId}/resend`, dto);
      return data.data as { message: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment', assessmentId] });
      qc.invalidateQueries({ queryKey: ['assessment-history', assessmentId] });
    },
  });
}

// ── Begin Review ──────────────────────────────────────────────────────────────

export function useBeginReview(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(`/assessments/${assessmentId}/begin-review`);
      return data.data as Assessment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment', assessmentId] });
    },
  });
}

// ── VA.3.7: tenant per-question bypass override ───────────────────────────────

export function useAddBypassOverride(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ questionId, notes }: { questionId: string; notes?: string }) => {
      const { data } = await apiClient.post(
        `/assessments/${assessmentId}/bypass-overrides/${questionId}`,
        { override_notes: notes ?? null },
      );
      return data.data as { id: string; questionId: string; overriddenAt: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment', assessmentId] });
    },
  });
}

// ── Flag question ─────────────────────────────────────────────────────────────

export function useFlagQuestion(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ responseId, flagged }: { responseId: string; flagged: boolean }) => {
      const { data } = await apiClient.patch(
        `/assessments/${assessmentId}/responses/${responseId}`,
        { is_flagged: flagged },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment', assessmentId] });
    },
  });
}
